import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findMarriageCountry, MARRIAGE_ABROAD } from "@/data/marriage-abroad";
import CountryDeepContent from "@/components/CountryDeepContent";
import { normalizeCountrySlug } from "@/lib/country-hague";

export const Route = createFileRoute("/en/marriage/abroad/$country")({
  loader: ({ params }) => {
    const c = findMarriageCountry(params.country);
    if (!c) throw notFound();
    return { c };
  },
  head: ({ params }) => {
    const c = findMarriageCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Not found", "/en/marriage/abroad/$country", "en")}, { name: "robots", content: "noindex" }] };
    const path = `/en/marriage/abroad/${c.slug}`;
    const TITLE = `Marry in ${c.en} as a Thai Citizen — 2026 Guide | NYC`;
    const DESC = `Complete guide for Thai citizens marrying in ${c.en}: ${c.hague ? "Apostille accepted": "Embassy Legalization required"} · translated into ${c.translationLang} · waiting period ${c.waitingPeriodDays} days · ${c.sameSexRecognized ? "same-sex marriage recognized": "opposite-sex only"} · ${c.notes} — Get your checklist in minutes.`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/marriage/abroad/$country", "en")}, { name: "description", content: ctaDesc(DESC, "/en/marriage/abroad/$country", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/marriage/abroad/$country", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/marriage/abroad/$country", "en")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/marriage/abroad/${c.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Marriage Abroad — ${c.en}`, priceFromThb: 8500,
        steps: [
          { name: "Eligibility check", text: `${c.affirmationRequired ? "Affirmation of Freedom to Marry from Thai MFA required" : "No Affirmation required"} · waiting period ${c.waitingPeriodDays} days` },
          { name: "Gather Thai documents", text: "Birth cert, house registration, ID card, single-status certificate (Kor.Ror.22), name change (if any)" },
          { name: "Translate + MFA + Apostille", text: `Translate into ${c.translationLang} + Thai MFA + ${c.hague ? "Apostille (Hague, in force 15 Dec 2025)" : "Embassy Legalization"}` },
          { name: "File Notice / Registry", text: `Submit to ${c.registryTh} — waiting period ${c.waitingPeriodDays} days per local law` },
          { name: "Register and record", text: "Receive Marriage Certificate + record at Thai Embassy for Thai-side recognition" },
        ],
        faqs: [
          { q: `What do Thai citizens need to marry in ${c.en}?`, a: `Core Thai docs + translation into ${c.translationLang} + ${c.hague ? "Apostille" : "Embassy Legalization"} · waiting period ${c.waitingPeriodDays} days` },
          { q: "What is Affirmation of Freedom to Marry?", a: c.affirmationRequired ? "A Thai MFA certificate confirming single status abroad — mandatory for this country." : "Not required for this country." },
          { q: "Translation language?", a: c.translationLang },
          { q: "Same-sex marriage recognized?", a: c.sameSexRecognized ? "Yes" : "Not yet." },
          { q: "Do we record it back in Thailand?", a: "Recommended — file the foreign marriage record at the Thai Embassy or at your amphoe office in Thailand so it has legal effect in Thailand (tax, visa, spouse rights)." },
          { q: "Total NYC package cost?", a: "Full document bundle — translation, notary, MFA legalization, and Apostille — runs ฿8,500–15,000 depending on how many source documents (birth certificate, single-status affidavit, divorce decree) your destination registry demands. This excludes the destination country's own registry fee, sworn translator fee on arrival, and courier costs; NYC returns a locked itemised quote within 30 minutes via LINE @nycli or +66 83-249-4999." },
          { q: "Total timeline?", a: `Thailand-side prep 5–10 days + waiting period ${c.waitingPeriodDays} days in ${c.en} + wedding day.` },
          { q: "Special notes?", a: c.notes },
        ],
        breadcrumbs: [
          { name: "Home", path: "/en" },
          { name: "Marriage", path: "/en/marriage" },
          { name: `Marriage Abroad — ${c.en}`, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Country not found</h1>      <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
    </main>
  ),
});

function Page() {
  const { c } = Route.useLoaderData();
  return (
    <>
    <ServiceLanding
      badge={`Marriage Abroad · ${c.en}`}
      title={`Marry in ${c.en} — Thai citizen guide`}
      subtitle={c.notes}
      quickAnswer={{
        q: `What do Thai citizens need to marry in ${c.en}?`,
        a: `Core Thai docs (birth cert, house registration, single-status) translated into ${c.translationLang} + ${c.hague ? "Apostille (Hague)" : "Embassy Legalization"} + ${c.affirmationRequired ? "Affirmation of Freedom to Marry from Thai MFA" : "no Affirmation"} · waiting period ${c.waitingPeriodDays} days · ${c.sameSexRecognized ? "same-sex recognized" : "opposite-sex only"}.`,
      }}
      heroBullets={[
        `${c.hague ? "✓ Apostille (Hague)" : "✗ Embassy Legalization"} — ${c.hague ? "saves time" : "extra step"}`,
        `Translated into ${c.translationLang}`,
        `Waiting period ${c.waitingPeriodDays} days`,
        `${c.sameSexRecognized ? "Same-sex marriage recognized" : "Opposite-sex only"}`,
      ]}
      whoFor={{
        th: [`Thai citizens marrying in ${c.en}`, "Couples living abroad", `Thai nationals marrying ${c.en} citizens`],
        foreign: [`Foreign spouses of Thai nationals marrying in ${c.en}`],
      }}
      steps={[
        { name: "Eligibility", text: `Waiting period ${c.waitingPeriodDays} days · ${c.affirmationRequired ? "Affirmation required" : "no Affirmation"}` },
        { name: "Thai docs", text: "Birth, house registration, ID, single-status" },
        { name: "Translate + MFA + chain", text: `${c.translationLang} + MFA + ${c.hague ? "Apostille" : "Embassy"}` },
        { name: `File at ${c.registryTh}`, text: `Notice/application per ${c.en} law` },
        { name: "Register + record in TH", text: "Receive certificate + file with Thai Embassy" },
      ]}
      pricing={[
        { label: "Full document package", price: "8,500–15,000", note: "translate + MFA + Apostille" },
        { label: "Affirmation of Freedom to Marry", price: "800", note: "MFA fee + processing" },
        { label: "Local sworn translation (if required)", price: "2,400–2,800/page", note: `${c.translationLang}` },
        { label: "Record marriage in Thailand", price: "1,500", note: "after return" },
      ]}
      faqs={[
        { q: "What is the waiting period?", a: `Statutory notice period in ${c.en} — ${c.waitingPeriodDays} days.` },
        { q: "Apostille vs Embassy Legalization?", a: c.hague ? `${c.en} is a Hague member — Apostille accepted directly, saving time and cost.` : `${c.en} is not a Hague member — Embassy Legalization at the ${c.en} embassy in Thailand is required.` },
        { q: "Same-sex marriage?", a: c.sameSexRecognized ? "Recognized — process is identical to opposite-sex couples." : "Not yet recognized." },
        { q: "Special notes?", a: c.notes },
        { q: "What must we do back in Thailand?", a: "Record the foreign marriage at the Thai Embassy or amphoe office in Thailand for legal effect (tax, visa, spouse rights)." },
      ]}
      relatedLinks={[
        { to: "/en/marriage", label: "← Marriage hub" },
        ...MARRIAGE_ABROAD.filter((x) => x.slug !== c.slug).slice(0, 5).map((x) => ({
          to: `/en/marriage/abroad/${x.slug}` as const, label: `Marry in ${x.en}`,
        })) as any,
        { to: "/en/apostille", label: "Apostille service" },
        { to: "/en/embassy", label: "Embassy Legalization" },
      ]}
    />
    <CountryDeepContent
      lang="en"
      service="marriage"
      countryTh={c.th}
      countryEn={c.en}
      hague={c.hague}
      priceFromTh={8500}
      turnaround={`${c.waitingPeriodDays} days + 5–10 business days`}
      slug={normalizeCountrySlug(c.slug)}
    />
    </>
  );
}
